import axiosInstance from "@/api/axiosInstance";
import {
  KiteModel,
  KiteModelPaginatedResponse,
  KiteModelRequestDTO,
} from "@/common/model/KiteModel";
import { ENDPOINTS } from "@/constants/api";
import { requestHandler } from "@/helpers/requestHandler";

// Re-exportar KiteModel para mantener compatibilidad con componentes existentes
export type { KiteModel };

export const KiteModelService = {
  /**
   * Get paginated models with sorting and filtering options
   * @param limit - Number of results per page
   * @param sortField - Field to sort by
   * @param sortOrder - Sort direction
   * @param page - Page number
   * @param searchValue - Optional search term
   * @returns Paginated response with models
   */
  async getModels(
    limit: number = 10,
    sortField: "model" | "kitebrandname" | "geartypename" = "model",
    sortOrder: "asc" | "desc" = "asc",
    page: number = 1,
    searchValue?: string
  ): Promise<KiteModelPaginatedResponse> {
    return requestHandler<KiteModelPaginatedResponse>(async () => {
      let url = `${ENDPOINTS.GEAR.MODELS_PAGINATED}?limit=${limit}&sortField=${sortField}&sortOrder=${sortOrder}&page=${page}`;

      if (searchValue) {
        url += `&searchval=${encodeURIComponent(searchValue)}`;
      }

      const response = await axiosInstance.get(url);
      const data = response.data;

      // Asignar totalItems como alias de totalModels para compatibilidad con el componente genérico
      if (data && typeof data.totalModels === "number") {
        data.totalItems = data.totalModels;
      }

      return data;
    }, "KiteModelService -> getModels");
  },

  /**
   * Search for models by name
   * @param searchValue - The search term (minimum 3 characters)
   * @param limit - Maximum number of results to return
   * @returns Array of matching models
   */
  async searchModels(
    searchValue: string,
    limit: number = 10
  ): Promise<KiteModel[]> {
    // Only perform the search if there are at least 3 characters
    if (searchValue.length < 3) return [];

    return requestHandler<KiteModel[]>(async () => {
      const response = await axiosInstance.get(
        `${ENDPOINTS.GEAR.MODELS_PAGINATED}?searchval=${encodeURIComponent(
          searchValue
        )}&limit=${limit}&sortField=model&sortOrder=asc`
      );
      return response.data?.data || [];
    }, "KiteModelService -> searchModels");
  },

  /**
   * Fetch a model by its ID
   * @param modelId - The ID of the model to fetch
   * @returns The model or null if not found
   */
  async getModelById(modelId: number): Promise<KiteModel | null> {
    return requestHandler<KiteModel | null>(async () => {
      const response = await axiosInstance.get(
        `${ENDPOINTS.GEAR.MODELS}/${modelId}`
      );
      return response.data || null;
    }, "KiteModelService -> getModelById");
  },

  /**
   * Create a new model
   * @param modelData - The model data to create
   * @returns The created model or null if creation failed
   */
  async createModel(modelData: KiteModelRequestDTO): Promise<KiteModel | null> {
    return requestHandler<KiteModel | null>(async () => {
      const response = await axiosInstance.post(
        ENDPOINTS.GEAR.MODELS,
        modelData
      );
      return response.data || null;
    }, "KiteModelService -> createModel");
  },

  /**
   * Update an existing model
   * @param modelId - The ID of the model to update
   * @param modelData - The updated model data
   * @returns The updated model or null if update failed
   */
  async updateModel(
    modelId: number,
    modelData: KiteModelRequestDTO
  ): Promise<KiteModel | null> {
    return requestHandler<KiteModel | null>(async () => {
      const response = await axiosInstance.put(
        ENDPOINTS.GEAR.MODELS_UPDATE(modelId),
        modelData
      );
      return response.data || null;
    }, "KiteModelService -> updateModel");
  },

  /**
   * Delete a model
   * @param modelId - The ID of the model to delete
   * @returns True if deletion was successful, false otherwise
   */
  async deleteModel(modelId: number): Promise<boolean> {
    return requestHandler<boolean>(async () => {
      const response = await axiosInstance.delete(
        `${ENDPOINTS.GEAR.MODELS_DELETE(modelId)}`
      );
      return response.status >= 200 && response.status < 300;
    }, "KiteModelService -> deleteModel");
  },
};
